import React from 'react';
import { siteConfig } from '@/config/site.config';
import type { FaqItem } from '@/content/types';

/**
 * ============================================================================
 *  <Seo /> — config-driven, per-route document metadata.
 * ============================================================================
 *
 * Renders the page `<title>`, description, canonical URL, Open Graph / Twitter
 * cards, local-SEO geo tags, and the schema.org JSON-LD graph. Everything is
 * sourced from `siteConfig.seo` (see `src/config/seo.ts`), so rebranding the
 * site's SEO never requires touching a component.
 *
 * Uses **React 19's native document metadata support**: `<title>`, `<meta>`,
 * and `<link>` rendered here are automatically hoisted into `<head>`. Because
 * it is a normal component, each route can render its own `<Seo>` with
 * overrides — fixing the SPA limitation that routes could not change their
 * metadata.
 */
/** A single crumb in a `breadcrumbs` trail — see `SeoProps.breadcrumbs`. */
export interface SeoBreadcrumb {
    /** Visible label, e.g. "Latest News". */
    name: string;
    /** Site-root-relative path of this crumb, e.g. `/blog`. */
    path: string;
}

/** Article-specific metadata for blog/news posts — see `SeoProps.article`. */
export interface SeoArticle {
    /** ISO 8601 date/time the article was first published. */
    publishedTime: string;
    /** ISO 8601 date/time of the latest edit. Defaults to `publishedTime`. */
    modifiedTime?: string;
    /** Byline, e.g. "Quality Team". Rendered as a schema.org `Person`. */
    author: string;
}

export interface SeoProps {
    /**
     * Page-specific title. Combined with `siteConfig.seo.titleTemplate`
     * (`%s`). When omitted, `siteConfig.seo.defaultTitle` is used verbatim.
     */
    title?: string;
    /** Overrides the default meta description. */
    description?: string;
    /** Site-root-relative path of this route (e.g. `/about`). Defaults to `/`. */
    path?: string;
    /** Overrides the Open Graph image (absolute URL or root-relative path). */
    image?: string;
    /** When `true`, emits `noindex, nofollow` for this route. */
    noindex?: boolean;
    /**
     * Extra, page-specific keywords appended to `siteConfig.seo.keywords`
     * (e.g. a blog post's own topics). Omit to use only the site-wide list.
     */
    keywords?: string[];
    /**
     * Intermediate crumbs between "Home" and this page, rendered as a
     * schema.org `BreadcrumbList` (e.g. `[{ name: 'Latest News', path:
     * '/blog' }]` on a blog post). "Home" and the current page — using
     * `title` — are added automatically; omit this prop for pages one level
     * below Home (their trail is just Home → page).
     */
    breadcrumbs?: SeoBreadcrumb[];
    /**
     * Marks this route as an article (blog/news post): switches
     * `og:type`/Twitter to article semantics, adds `article:*` meta tags,
     * and renders a schema.org `BlogPosting` node instead of `WebPage`.
     */
    article?: SeoArticle;
    /**
     * Question/answer pairs rendered as a schema.org `FAQPage` node — the
     * single highest-leverage structured-data type for LLM/AI answer
     * engines (ChatGPT, Gemini, Perplexity, ...), which favor content
     * already shaped as a direct question + a self-contained answer. Pass
     * the same data used to render the visible FAQ section (e.g.
     * `faqContent.items` from `src/content/faq.ts`) so structured data never
     * drifts from what a visitor actually sees. Omit for routes with no FAQ.
     */
    faq?: FaqItem[];
}

const { seo, brand, social } = siteConfig;

/** Resolve an absolute URL from an absolute URL or a site-root-relative path. */
function toAbsoluteUrl(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    const suffix = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${seo.siteUrl}${suffix}`;
}

const Seo: React.FC<SeoProps> = ({
    title,
    description = seo.description,
    path = '/',
    image,
    noindex = false,
    keywords = [],
    breadcrumbs,
    article,
    faq,
}) => {
    const fullTitle = title
        ? seo.titleTemplate.replace('%s', title)
        : seo.defaultTitle;
    const canonical = toAbsoluteUrl(path);
    const ogImage = toAbsoluteUrl(image ?? seo.ogImage);
    const robots = noindex ? 'noindex, nofollow' : 'index, follow';
    const pageKeywords = keywords.length > 0 ? [...seo.keywords, ...keywords] : seo.keywords;
    const ogType = article ? 'article' : 'website';

    const breadcrumbList = breadcrumbs && [
        {
            '@type': 'BreadcrumbList',
            '@id': `${canonical}#breadcrumb`,
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${seo.siteUrl}/` },
                ...breadcrumbs.map((crumb, index) => ({
                    '@type': 'ListItem',
                    position: index + 2,
                    name: crumb.name,
                    item: toAbsoluteUrl(crumb.path),
                })),
                {
                    '@type': 'ListItem',
                    position: breadcrumbs.length + 2,
                    name: title ?? seo.defaultTitle,
                    item: canonical,
                },
            ],
        },
    ];

    const jsonLd = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'Organization',
                '@id': `${seo.siteUrl}/#organization`,
                name: brand.name,
                url: `${seo.siteUrl}/`,
                logo: {
                    '@type': 'ImageObject',
                    url: toAbsoluteUrl(brand.logo),
                    width: String(seo.organization.logoWidth),
                    height: String(seo.organization.logoHeight),
                },
                contactPoint: {
                    '@type': 'ContactPoint',
                    telephone: seo.organization.contactPoint.telephone,
                    contactType: seo.organization.contactPoint.contactType,
                    areaServed: seo.organization.contactPoint.areaServed,
                    availableLanguage:
                        seo.organization.contactPoint.availableLanguage,
                },
                address: {
                    '@type': 'PostalAddress',
                    addressLocality: seo.organization.address.locality,
                    addressCountry: seo.organization.address.country,
                },
                description: seo.description,
                slogan: brand.tagline,
                ...(social.length > 0 ? { sameAs: social.map((link) => link.href) } : {}),
            },
            {
                '@type': 'WebSite',
                '@id': `${seo.siteUrl}/#website`,
                url: `${seo.siteUrl}/`,
                name: brand.name,
                publisher: { '@id': `${seo.siteUrl}/#organization` },
                description: seo.description,
            },
            ...(article
                ? [
                      {
                          '@type': 'BlogPosting',
                          '@id': `${canonical}#article`,
                          mainEntityOfPage: canonical,
                          headline: title ?? seo.defaultTitle,
                          description,
                          image: ogImage,
                          datePublished: article.publishedTime,
                          dateModified: article.modifiedTime ?? article.publishedTime,
                          author: { '@type': 'Person', name: article.author },
                          publisher: { '@id': `${seo.siteUrl}/#organization` },
                      },
                  ]
                : []),
            ...(breadcrumbList ?? []),
            ...(faq && faq.length > 0
                ? [
                      {
                          '@type': 'FAQPage',
                          '@id': `${canonical}#faq`,
                          mainEntity: faq.map((item) => ({
                              '@type': 'Question',
                              name: item.question,
                              acceptedAnswer: {
                                  '@type': 'Answer',
                                  text: item.answer,
                              },
                          })),
                      },
                  ]
                : []),
        ],
    };

    return (
        <>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {pageKeywords.length > 0 && (
                <meta name="keywords" content={pageKeywords.join(', ')} />
            )}
            <meta name="robots" content={robots} />
            <link rel="canonical" href={canonical} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={canonical} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content={String(seo.ogImageWidth)} />
            <meta property="og:image:height" content={String(seo.ogImageHeight)} />
            <meta property="og:image:alt" content={seo.ogImageAlt} />
            <meta property="og:site_name" content={brand.name} />
            <meta property="og:locale" content={seo.locale} />
            {article && (
                <meta property="article:published_time" content={article.publishedTime} />
            )}
            {article && (
                <meta
                    property="article:modified_time"
                    content={article.modifiedTime ?? article.publishedTime}
                />
            )}
            {article && <meta property="article:author" content={article.author} />}

            {/* Twitter */}
            <meta name="twitter:card" content={seo.twitterCard} />
            <meta name="twitter:url" content={canonical} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:image:alt" content={seo.ogImageAlt} />

            <meta name="apple-mobile-web-app-title" content={brand.name} />
            <meta name="application-name" content={brand.name} />

            {/* Local SEO */}
            {seo.geo && (
                <meta name="geo.region" content={seo.geo.region} />
            )}
            {seo.geo && (
                <meta name="geo.placename" content={seo.geo.placename} />
            )}
            {seo.geo && (
                <meta name="geo.position" content={seo.geo.position} />
            )}
            {seo.geo && <meta name="ICBM" content={seo.geo.icbm} />}

            {/* Schema.org / JSON-LD */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
            />
        </>
    );
};

export default Seo;
